import ItemPlacement from 'components/framework/models/ItemPlacement';
import { batch } from '@aha-app/react-easy-state';
import { ApplicationController } from '@aha-app/mvc';
import type ListDropHandler from '../ListDropHandler';
import type DraggableItem from '../DraggableItem';
import { type ItemComponentProps } from '../components/types';
import type React from 'react';
import { type SelectableItemController } from '../SelectableItem';

const VOID_LIST_KEY = 'VOID_LIST_KEY';

type DragControllerDropData = {
  dropHandler: ListDropHandler<SelectableItemController>;
  listKey: string;
  placement: ItemPlacement;
};

type DragControllerProps = {};
type DragControllerState = {
  items: Set<any>;
  dragItems: Set<DraggableItem<any>>;
  dragging: boolean;
  dropData: DragControllerDropData;
};

export const createEmptyState = (): DragControllerState => ({
  // Items being dragged
  items: new Set(),
  dragItems: new Set(),
  dragging: false,
  dropData: { dropHandler: null, listKey: null, placement: null },
});

class DragController extends ApplicationController<
  DragControllerState,
  DragControllerProps
> {
  dragComponentRegistry: Map<string, React.FC<ItemComponentProps>>;

  static initialState = createEmptyState();

  async initialize() {
    this.reset();

    this.dragComponentRegistry = new Map();
  }

  get dragItems() {
    return Array.from(this.state.dragItems);
  }

  get hoverListKey() {
    return this.state.dragging ? this.state.dropData.listKey : null;
  }

  reset() {
    Object.assign(this.state, createEmptyState());
  }

  isDraggingItem(item) {
    return this.state.dragging && this.state.items.has(item);
  }

  isDraggingDraggableItem(dragItem) {
    return this.isDraggingItem(dragItem.item);
  }

  get isHoveringOverDropHandler() {
    return (
      this.state.dragging &&
      this.state.dropData.dropHandler !== null &&
      this.state.dropData.listKey !== null
    );
  }

  actionStartDrag(items, dropData) {
    batch(() => {
      this.reset();

      items.forEach(item => {
        item.onBegin();
        this.state.items.add(item.item);
        this.state.dragItems.add(item);
      });
      this.state.dragging = true;
      this.actionUpdateDropData(dropData);
    });
  }

  actionUpdateDropData(dropData: DragControllerDropData) {
    Object.assign(this.state.dropData, dropData);
  }

  actionHoverOverDropHandler({
    dropHandler,
    listKey,
    placement,
  }: DragControllerDropData) {
    const { dropData } = this.state;
    if (
      listKey === dropData.listKey &&
      dropData?.placement.isEqual(placement)
    ) {
      return;
    }

    this.actionUpdateDropData({
      dropHandler,
      listKey,
      placement,
    });
  }

  actionSetVoidDropTarget() {
    this.actionHoverOverDropHandler({
      dropHandler: null,
      listKey: VOID_LIST_KEY,
      placement: ItemPlacement.top(),
    });
  }

  actionFinishDrop() {
    const { listKey } = this.state.dropData;

    if (listKey === VOID_LIST_KEY) {
      this.actionRemoveItem();
    } else {
      this.actionDropAtLastHoverLocation();
    }
  }

  /**
   * Called when a drop is completed on a list drag. The state of items and drop positions will
   * be automatically extracted from the drag controller state to complete the drop.
   */
  actionDropAtLastHoverLocation() {
    const { dragItems, dropData } = this.state;
    const { dropHandler, placement } = dropData;

    if (dropHandler) {
      dropHandler.onDropAtDropPlacement({
        dragItems: Array.from(dragItems),
        placement,
      });

      this.actionStopDrag();
    } else {
      this.actionCancelDrag();
    }
  }

  /**
   * Called when dragged items are dropped in a "void" drop handler, and we want to remove the
   * items from the old location, but they aren't being moved to a new location.
   */
  actionRemoveItem() {
    const { dropHandler } = this.state.dropData;

    if (dropHandler) {
      const { dragItems } = this.state;
      dropHandler.onRemoveItems({ dragItems: Array.from(dragItems) });
    }

    this.reset();
  }

  actionCancelDrag() {
    // Cancel the original drag
    this.dragItems.forEach(dragItem => dragItem.onCancel());

    this.reset();
  }

  actionStopDrag() {
    this.reset();
  }

  /**
   * Drag Component Registry
   *
   * We keep a registry of the various components used to render a type of DraggableItem. This makes it
   * easy to have multiple types of items being draggable on a single screen, without various controllers
   * and components having to know how to render themselves.
   *
   * The `key`` is the name of the `DraggableItem` class, and the value is the component used to render
   * those items.
   */
  registerDragComponent(
    dragClass: typeof DraggableItem,
    component: React.FC<ItemComponentProps>
  ) {
    this.dragComponentRegistry.set(dragClass.name, component);

    return () => this.dragComponentRegistry.delete(dragClass.name);
  }

  getDragComponentForItem(dragItem: DraggableItem) {
    return this.dragComponentRegistry.get(dragItem.type);
  }
}

export const sharedDragController = new DragController();
sharedDragController.internalInitialize(null, createEmptyState());

export default DragController;
